import { useEffect } from 'react';
import { MessagesTypes } from '@frontend/api-messaging';
import { BulkTextPrefixes } from '@frontend/tracking-prefixes';
import { DateRangeField, FormFieldActionTypes, useForm } from '@frontend/design-system';
import { SwitchedFilter } from './switched-filter';
import { formatRange, rangeHasDate } from './utils';

interface Props {
  filters: MessagesTypes.Filters;
  setFilters: (filters: MessagesTypes.Filters) => void;
  filterType: keyof MessagesTypes.Filters;
}

export const DateFilter = ({ filters, setFilters, filterType, ...rest }: Props) => {
  const updateFilter = (values?: string[]) => {
    if (values) {
      setFilters({ ...filters, [filterType]: formatRange(values) });
    } else {
      const copy = { ...filters };
      delete copy[filterType];
      setFilters(copy);
    }
  };

  const { getFieldProps, seedValues } = useForm({
    fields: {
      filter: {
        type: 'dateRange',
        required: true,
      },
      enabled: {
        type: 'switch',
      },
    },
    fieldStateReducer: (state, action) => {
      if (action.type === FormFieldActionTypes.Update && action.payload.name === 'filter') {
        // enable toggle when selecting at least one
        if (rangeHasDate(state.filter.value as string[]) && !state.enabled.value) {
          return {
            ...state,
            enabled: {
              ...state.enabled,
              value: true,
            },
          };
        }
      }

      if (action.type === FormFieldActionTypes.Update && action.payload.name === 'enabled') {
        // removing selections when toggle is turned off
        if (!state.enabled.value) {
          return {
            ...state,
            enabled: {
              ...state.enabled,
              value: false,
            },
            filter: {
              ...state.filter,
              value: ['', ''],
            },
          };
        }
      }
      return state;
    },
  });

  useEffect(() => {
    seedValues({
      filter: formatRange(filters[filterType] as string[] | undefined, 'MM/DD/YYYY'),
      enabled: !!((filters[filterType] as [string, string])?.[0] || (filters[filterType] as [string, string])?.[1]),
    });
  }, []);

  useEffect(() => {
    const value = getFieldProps('filter').value;
    const filterValue = rangeHasDate(value) ? value : undefined;
    updateFilter(filterValue);
  }, [getFieldProps('filter').value]);

  useEffect(() => {
    if (!getFieldProps('enabled').value) {
      updateFilter(undefined);
    }
  }, [getFieldProps('enabled').value]);

  return (
    <div {...rest}>
      <SwitchedFilter
        getFieldProps={getFieldProps}
        data-trackingid={`${BulkTextPrefixes.Recipients}-${filterType}-filter-input`}
      >
        <DateRangeField label='' {...getFieldProps('filter')} />
      </SwitchedFilter>
    </div>
  );
};
